import React, { useEffect, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Steps } from './Steps';
import { HomeCareLocationStep } from './steps/HomeCareLocationStep';
import { HomeCareServiceStep } from './steps/HomeCareServiceStep';
import { HomeCarePaymentStep } from './steps/HomeCarePaymentStep';
import { ProfessionalLoader } from './components/ProfessionalLoader';
import {
  Container,
  FormSection,
  SummaryCard,
  Button
} from './StyledComponents';
import { animations } from './animations';

const HomeCareFlow = ({ socket }) => {
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [location, setLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [isLoading, setIsLoading] = useState(false);
  const [requestStatus, setRequestStatus] = useState('');
  const [error, setError] = useState('');
  const [requestDetails, setRequestDetails] = useState(null);

  useEffect(() => {
    if (socket) {
      // Socket listeners for home care flow
      socket.on('availableHomeCareServices', handleServicesAvailable);
      socket.on('homeCareServicesError', handleServicesError);
      socket.on('homeCareRequestCreated', handleRequestCreated);
      socket.on('homeCareRequestError', handleRequestError);
      socket.on('homeCareNoProviders', handleNoProviders);
      socket.on('homeCareNoResponse', handleNoResponse);
      socket.on('homeCareRequestAccepted', handleRequestAccepted);
      socket.on('incomingHomeCareRequest', handleIncomingRequest);

      return () => {
        socket.off('availableHomeCareServices');
        socket.off('homeCareServicesError');
        socket.off('homeCareRequestCreated');
        socket.off('homeCareRequestError');
        socket.off('homeCareNoProviders');
        socket.off('homeCareNoResponse');
        socket.off('homeCareRequestAccepted');
        socket.off('incomingHomeCareRequest');
      };
    }
  }, [socket]);

  const handleServicesAvailable = (data) => {
    console.log('Available services received:', data);
    setAvailableServices(data.services || []);
    console.log('======called here ======1=======');
    setIsLoading(false);
    setStep(2); // Move to service selection step
  };

  const handleServicesError = (data) => {
    console.log('Services error:', data);
    setError(data.message || 'Failed to get available services');
    console.log('======called here ======2=======');
    setIsLoading(false);
  };

  const handleRequestCreated = (data) => {
    console.log('Request created:', data);
    setRequestStatus('searching');
    console.log('======called here ======3=======');
    // Keep loading - don't stop here, continue until we get final response
    setStep(4); // Move to status step but keep loading
  };

  const handleRequestError = (data) => {
    console.log('Request error:', data);
    setError(data.message || 'Failed to create request');
    console.log('======called here ======4=======');
    setIsLoading(false); // Stop loading on error
  };

  const handleNoProviders = (data) => {
    console.log('No providers:', data);
    setError(data.message || 'No home care providers available in your area');
    console.log('======called here ======5=======');
    setIsLoading(false); // Stop loading when no providers found
  };

  const handleNoResponse = (data) => {
    console.log('No response:', data);
    setError(data.message || 'No providers responded to your request');
    console.log('======called here ======6=======');
    setIsLoading(false); // Stop loading when no response
  };

  const handleRequestAccepted = (data) => {
    console.log('Request accepted:', data);
    setRequestStatus('accepted');
    // Store driver and service details for display
    setRequestDetails({
      driverDetails: data.driverDetails,
      serviceDetails: data.serviceDetails,
      acceptanceMessage: data.acceptanceMessage
    });
    console.log('======called here ======7=======');
    setIsLoading(false); // Stop loading when request is accepted
  };

  const handleIncomingRequest = (data) => {
    console.log('Incoming request (should not happen on patient side):', data);
  };

  const validatePhone = () => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return false;
    }
    if (!phone.startsWith('251') && !phone.startsWith('09') && !phone.startsWith('07')) {
      setPhoneError('Invalid phone number format');
      return false;
    }
    if (phone.startsWith('251') && phone.length !== 12) {
      setPhoneError('Invalid phone number length');
      return false;
    }
    if ((phone.startsWith('09') || phone.startsWith('07')) && phone.length !== 10) {
      setPhoneError('Invalid phone number length');
      return false;
    }
    return true;
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    setPhoneError('');
    setError('');
  };

  const handleLocationChange = async (value) => {
    setLocationError('');
    
    if (!value || !value.label) {
      setSelectedLocation(null);
      setLocation(null);
      return;
    }
    
    try {
      const { geocodeByAddress, getLatLng } = await import('react-google-places-autocomplete');
      const results = await geocodeByAddress(value.label);
      const latLng = await getLatLng(results[0]);
      
      const locationData = {
        lat: latLng.lat,
        lng: latLng.lng,
        address: value.label
      };
      
      setSelectedLocation(value);
      setLocation(locationData);
      console.log('Location set:', locationData);
    } catch (err) {
      console.error('Geocoding error:', err);
      setLocationError('Failed to locate address');
    }
  };

  const handleNextFromLocation = () => {
    if (!phone) {
      setPhoneError('Phone number is required');
      return;
    }
    if (!validatePhone()) {
      return;
    }
    if (!selectedLocation) {
      setLocationError('Please select a service location');
      return;
    }
    
    // Get available services based on location
    setIsLoading(true);
    
    if (socket) {
      socket.emit('getAvailableHomeCareServices', JSON.stringify({
        phone,
        latitude: location.lat,
        longitude: location.lng
      }));
    } else {
      // Simulate available services for testing
      setTimeout(() => {
        setAvailableServices([
          {
            vehicleTypeId: 1,
            serviceName: 'General Practitioner Visit',
            fixedPrice: 50,
            availableDrivers: 3
          },
          {
            vehicleTypeId: 2,
            serviceName: 'Nursing Care',
            fixedPrice: 40,
            availableDrivers: 2
          },
          {
            vehicleTypeId: 3,
            serviceName: 'Wound Dressing',
            fixedPrice: 30,
            availableDrivers: 1
          }
        ]);
        console.log('======called here ======8=======');
        setIsLoading(false);
        setStep(2); // Move to next step
      }, 2000);
    }
  };

  const handleGetLocation = () => {
    if (!validatePhone()) {
      return;
    }

    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setLocation({
          lat: latitude,
          lng: longitude,
          address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
        });
        
        // Emit location to get available services
        if (socket) {
          socket.emit('getAvailableHomeCareServices', JSON.stringify({
            phone,
            latitude,
            longitude
          }));
        } else {
          // If no socket, simulate available services for testing
          setTimeout(() => {
            setAvailableServices([
              {
                vehicleTypeId: 1,
                serviceName: 'General Practitioner Visit',
                fixedPrice: 50,
                availableDrivers: 3
              },
              {
                vehicleTypeId: 2,
                serviceName: 'Nursing Care',
                fixedPrice: 40,
                availableDrivers: 2
              },
              {
                vehicleTypeId: 3,
                serviceName: 'Wound Dressing',
                fixedPrice: 30,
                availableDrivers: 1
              }
            ]);
            // Professional loading transition
            setTimeout(() => {
              console.log('======called here ======9=======');
              setIsLoading(false);
              setStep(2); // Move to next step
            }, 500);
          }, 2000);
        }
      },
      (error) => {
        setError('Unable to get your location. Please enable location services.');
        console.log('======called here ======10=======');
        setIsLoading(false);
      }
    );
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
  };

  const handleSubmitRequest = () => {
    if (!paymentMethod) {
      setError('Please select a payment method');
      return;
    }

    setIsLoading(true);
    setError(''); // Clear any previous errors
    
    if (socket) {
      // Send request to backend - loading will be stopped by socket event handlers
      socket.emit('requestHomeCare', JSON.stringify({
        phone,
        vehicleTypeId: selectedService.vehicleTypeId,
        latitude: location.lat,
        longitude: location.lng,
        locationName: location.address,
        paymentMethod
      }));
      // Don't set setIsLoading(false) here! Let the socket handlers control it
    } else {
      // Simulate request submission for testing
      setTimeout(() => {
        setRequestStatus('searching');
        setStep(4);
        // Simulate a longer backend response time for testing
        setTimeout(() => {
          console.log('======called here ======11=======');
          // Simulate different possible outcomes for testing
          const outcomes = ['accepted', 'no_providers', 'no_response'];
          const randomOutcome = outcomes[Math.floor(Math.random() * outcomes.length)];
          
          if (randomOutcome === 'accepted') {
            setRequestStatus('accepted');
          } else if (randomOutcome === 'no_providers') {
            setError('No home care providers available in your area');
          } else {
            setError('No providers responded to your request');
          }
          
          setIsLoading(false); // Only stop loading after simulated backend response
        }, 5000); // Keep loading for 5 seconds after moving to step 4
      }, 2000);
    }
  };

  const navigateToStep = (targetStep) => {
    if (canNavigateToStep(targetStep)) {
      setStep(targetStep);
    }
  };

  const canNavigateToStep = (targetStep) => {
    switch (targetStep) {
      case 1:
        return true;
      case 2:
        return phone && location;
      case 3:
        return selectedService;
      case 4:
        return paymentMethod;
      default:
        return false;
    }
  };

  const getSteps = () => [
    { label: 'Location', number: 1 },
    { label: 'Service', number: 2 },
    { label: 'Payment', number: 3 },
    { label: 'Status', number: 4 }
  ];

  // Get loader content based on current step and status
  const getLoaderContent = () => {
    if (step === 1) {
      return {
        title: "Getting Your Location",
        subtitle: "Finding available home care services in your area...",
        currentStep: 1
      };
    }
    if (step === 3 || (step === 4 && requestStatus === 'searching')) {
      return {
        title: "Processing Your Request",
        subtitle: "Creating your home care request and finding providers...",
        currentStep: 3
      };
    }
    return null;
  };

  const renderStatusStep = () => {
    if (requestStatus === 'completed') {
      return (
        <SummaryCard status="success">
          <h3>Service Completed Successfully!</h3>
          <p>Your home care service has been completed. Thank you for using our service.</p>
          <Button variant="primary" onClick={() => {
            setStep(1);
            setPhone('');
            setLocation(null);
            setSelectedService(null);
            setPaymentMethod('Cash');
            setRequestStatus('');
            setAvailableServices([]);
            setSelectedLocation(null);
            setError('');
          }}>
            Book Another Service
          </Button>
        </SummaryCard>
      );
    }

    if (requestStatus === 'started') {
      return (
        <SummaryCard status="warning">
          <h3>Service in Progress</h3>
          <p>Your home care provider is currently providing the service.</p>
          <div className="status-indicator">
            <div className="spinning">⟳</div>
            <span>Service ongoing...</span>
          </div>
        </SummaryCard>
      );
    }

    if (requestStatus === 'accepted') {
      return (
        <SummaryCard status="success">
          <h3>Request Accepted!</h3>
          <p>A home care provider has been assigned to you and is on their way.</p>
          
          {requestDetails && requestDetails.driverDetails && (
            <div style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '1rem',
              margin: '1rem 0'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#1e40af' }}>Provider Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div><strong>Name:</strong> {requestDetails.driverDetails.name}</div>
                <div><strong>Phone:</strong> {requestDetails.driverDetails.phone}</div>
                <div><strong>Vehicle:</strong> {requestDetails.driverDetails.vehicle}</div>
                <div><strong>Plate:</strong> {requestDetails.driverDetails.plateNumber}</div>
                <div><strong>Rating:</strong> ⭐ {requestDetails.driverDetails.rating}/5</div>
                <div><strong>Experience:</strong> {requestDetails.driverDetails.experience}</div>
              </div>
              <div style={{ marginTop: '0.5rem' }}>
                <strong>Specialization:</strong> {requestDetails.driverDetails.specialization}
              </div>
            </div>
          )}
          
          {requestDetails && requestDetails.serviceDetails && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '8px',
              padding: '1rem',
              margin: '1rem 0'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#0ea5e9' }}>Service Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div><strong>Price:</strong> ${requestDetails.serviceDetails.estimatedPrice}</div>
                <div><strong>Payment:</strong> {requestDetails.serviceDetails.paymentMethod}</div>
                <div><strong>Duration:</strong> {requestDetails.serviceDetails.estimatedDuration}</div>
                <div><strong>Location:</strong> {requestDetails.serviceDetails.serviceLocation}</div>
              </div>
            </div>
          )}
          
          <div className="status-indicator">
            <div className="spinning">⟳</div>
            <span>Provider en route...</span>
          </div>
        </SummaryCard>
      );
    }

    // Only show processing status if there's no error
    if (!error) {
      return (
        <SummaryCard>
          <h3>Processing Your Request</h3>
          <p>We are looking for available home care providers in your area.</p>
          <div className="status-indicator">
            <div className="spinning">⟳</div>
            <span>Finding providers...</span>
          </div>
        </SummaryCard>
      );
    }

    // If there's an error, don't show the processing card - just return null
    // The error will be displayed by the error div below
    return null;
  };

  return (
    <Container>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '2rem',
          minHeight: 'calc(100vh - 4rem)',
          width: '100%',
        }}
      >
        <FormSection
          initial="initial"
          animate="animate"
          exit="exit"
          variants={animations.pageTransition}
          style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
          }}
        >
          <Steps 
            currentStep={step}
            navigateToStep={navigateToStep}
            canNavigateToStep={canNavigateToStep}
            steps={getSteps()}
          />
          
          <AnimatePresence mode="wait">
            {isLoading && getLoaderContent() && (() => {
              const loaderContent = getLoaderContent();
              return (
                <ProfessionalLoader
                  isVisible={isLoading}
                  title={loaderContent.title}
                  subtitle={loaderContent.subtitle}
                  showSteps={true}
                  currentStep={loaderContent.currentStep}
                  isHomeCare={true}
                />
              );
            })()}

            {!isLoading && step === 1 && (
              <HomeCareLocationStep
                phone={phone}
                phoneError={phoneError}
                onPhoneChange={handlePhoneChange}
                onLocationChange={handleLocationChange}
                onNext={handleNextFromLocation}
                isLoading={isLoading}
                selectedLocation={selectedLocation}
                locationError={locationError}
              />
            )}

            {!isLoading && step === 2 && (
              <HomeCareServiceStep
                availableServices={availableServices}
                selectedService={selectedService}
                onServiceSelect={handleServiceSelect}
                onPrevious={() => setStep(1)}
                onNext={() => setStep(3)}
              />
            )}

            {!isLoading && step === 3 && (
              <HomeCarePaymentStep
                selectedService={selectedService}
                paymentMethod={paymentMethod}
                onPaymentMethodChange={handlePaymentMethodChange}
                onPrevious={() => setStep(2)}
                onSubmit={handleSubmitRequest}
                isLoading={isLoading}
              />
            )}

            {!isLoading && step === 4 && renderStatusStep()}
          </AnimatePresence>

          {error && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              padding: '1rem',
              margin: '1rem 0',
              color: '#dc2626'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}
        </FormSection>
      </div>
    </Container>
  );
};

export default HomeCareFlow;